import { BotIcon, CpuIcon, UserRoundIcon, WorkflowIcon } from "lucide-react";
import { cn } from "../cn";
import { Badge } from "./badge";

// WORKFORCE (Andrei, 2026-10-26): every worker's name carries what it is — a digital employee
// (DE: seat, KPIs, budget), an agent (AG: a task executor) or a person — as a small badge with an
// icon. Display names may already carry the "DE · " / "AG · " label (ops names them so in
// chat and on tasks); `workerName` drops it wherever the badge is shown, so nothing reads
// "DE · DE · …".

// SYSTEM-AGENT (2026-10-29): `system` — the platform's own agent in a company, shown only in the
// operator console (SYS).
export type WorkerKind = "de" | "ag" | "person" | "system";

const LABELS: Record<WorkerKind, string> = {
  de: "DE",
  ag: "AG",
  person: "Person",
  system: "SYS",
};
const TITLES: Record<WorkerKind, string> = {
  de: "Digital employee",
  ag: "Agent",
  person: "Person",
  system: "System agent",
};
const ICONS = { de: BotIcon, ag: WorkflowIcon, person: UserRoundIcon, system: CpuIcon } as const;

/** The name without a leading "DE · " or "AG · " label (the badge says it). */
export function workerName(name: string): string {
  return name.replace(/^(?:(?:DE|AG)\s*·\s*)+/u, "");
}

export type WorkerBadgeProps = {
  kind: WorkerKind;
  size?: "sm" | "md";
  /** A person's badge is the icon alone unless this is set. */
  showPersonLabel?: boolean;
  /**
   * Read the kind out to assistive tech ("Digital employee"). Off by default: the badge sits
   * beside a name whose link or heading must keep the name as its accessible name, so it is
   * decorative there (the hover title still says it).
   */
  announce?: boolean;
  className?: string;
};

function WorkerBadge({
  kind,
  size = "md",
  showPersonLabel = false,
  announce = false,
  className,
}: WorkerBadgeProps) {
  const Icon = ICONS[kind];
  const label = kind === "person" && !showPersonLabel ? null : LABELS[kind];
  return (
    <Badge
      variant={
        kind === "de"
          ? "accent"
          : kind === "ag"
            ? "info"
            : kind === "system"
              ? "outline"
              : "neutral"
      }
      data-slot="worker-badge"
      data-kind={kind}
      title={TITLES[kind]}
      {...(announce ? { "aria-label": TITLES[kind] } : { "aria-hidden": true })}
      className={cn(size === "sm" ? "h-4 gap-0.5 px-1 text-[10px]" : null, className)}
    >
      <Icon aria-hidden="true" />
      {label ? <span aria-hidden="true">{label}</span> : null}
    </Badge>
  );
}

export { WorkerBadge };
